import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LegalPersonValidatedFormComponent } from '@app/components/legal-person-validated-form/legal-person-validated-form.component';
import { getLegalEntitiyData } from '@app/components/legal-person-validated-form/legal-person-validated-form.helper';
import {
  DocumentUploadFormGroup,
  getDocumentUploadFormGroup,
} from '@app/components/registration/document-upload-form/document-upload-form.helper';
import { getNewFileFormGroup, isFormValid } from '@app/helpers/form.helper';
import {
  FileUploadControl,
  LegalEntityFormGroup,
} from '@app/models/form.models';
import {
  ApprovalRepresentationTypes,
  FileType,
} from '@app/models/shared.enums';
import { AccountService } from '@app/services/account.service';
import { FileData } from '@app/services/file/upload.service';
import { LegalPersonService } from '@app/services/profile/legal-person.service';
import { AuthStore } from '@app/stores/auth.store';
import { GenericStore } from '@app/stores/generic.store';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { termsAndConditions } from 'src/app/+store/projects/constants';
import { DocumentUploadFormComponent } from '../../components/registration/document-upload-form/document-upload-form.component';
import { ProofOfProfessionalStatusFormComponent } from '../../components/registration/proof-of-professional-status-form/proof-of-professional-status-form.component';
import { WizardStepComponent } from '../../components/wizard/wizard-step/wizard-step.component';
import {
  StepType,
  WizardComponent,
  WizardStep,
} from '../../components/wizard/wizard.component';

enum RegistrationStepType {
  termsAndConditions,
  personalData,
  legalPersonData,
  proofOfProfessionalStatus,
  documentUpload,
}

@Component({
  selector: 'app-add-new-profile-wizard',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    WizardComponent,
    WizardStepComponent,
    DocumentUploadFormComponent,
    ProofOfProfessionalStatusFormComponent,
    LegalPersonValidatedFormComponent,
  ],
  templateUrl: './add-new-profile-wizard.component.html',
  styleUrl: './add-new-profile-wizard.component.scss',
})
export class AddNewProfileWizardComponent {
  private acceptTranslation = 'registrationWizard.iAccept';
  private declineTranslation = 'registrationWizard.decline';
  private cancelBtnTranslation = 'general.cancel';
  private perviousBtnTranslation = 'general.previous';
  private nextBtnTranslation = 'general.next';
  private finishBtnTranslation = 'registrationWizard.finishRegistration';

  private defaultWizardSteps: WizardStep<RegistrationStepType>[] = [
    {
      icon: '1',
      title: 'registrationWizard.legalPersonForm.dataOfLegalPerson',
      isActive: true,
      stepType: StepType.available,
      sectionType: RegistrationStepType.legalPersonData,
    },
    {
      icon: '2',
      title: 'registrationWizard.documentUploadForm.uploadingDocuments',
      isActive: false,
      stepType: StepType.disabled,
      sectionType: RegistrationStepType.documentUpload,
    },
  ];

  public items = signal<WizardStep<RegistrationStepType>[]>(
    this.defaultWizardSteps,
  );

  legalPersonForm: FormGroup<LegalEntityFormGroup>;
  documentUpload: FormGroup<DocumentUploadFormGroup>;
  proofOfProfessionalStatus: FormGroup<FileUploadControl>;

  contractText = termsAndConditions; //TODO: load from API

  protected stepIndex = signal<number>(1);
  protected prevButtonText = signal<string>(this.cancelBtnTranslation);
  protected nextButtonText = signal<string>(this.nextBtnTranslation);

  protected prevButtonHasChevron = signal<boolean>(false);
  protected nextButtonHasChevron = signal<boolean>(true);

  protected authStore = inject(AuthStore);
  protected genericStore = inject(GenericStore);

  protected legalPersonService = inject(LegalPersonService);

  private legalEntityId = signal<string | undefined>(undefined);

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private router: Router,
    private accountService: AccountService,
    private route: ActivatedRoute,
  ) {
    const profileType = route.snapshot.params['type'];

    if (profileType == 'professional') {
      this.professionalClientSelected();
    }

    this.legalPersonForm = getLegalEntitiyData(this.fb);
    this.documentUpload = getDocumentUploadFormGroup(fb);
    this.proofOfProfessionalStatus = getNewFileFormGroup(
      this.fb,
      FileType.userLegalEntityRightDocuProfessional,
    );

    effect(
      () => {
        if (this.stepIndex() == 1) {
          this.prevButtonHasChevron.set(false);
          this.nextButtonHasChevron.set(true);
          this.prevButtonText.set(this.cancelBtnTranslation);
          this.nextButtonText.set(this.nextBtnTranslation);
        }
        if (this.stepIndex() == 2) {
          this.prevButtonHasChevron.set(true);
          if (this.items().length === 2) {
            this.lastBtnIsFinish();
          } else {
            this.setPrevNextBtn();
          }
        }
        if (this.stepIndex() == 3) {
          this.prevButtonHasChevron.set(true);
          this.lastBtnIsFinish();
        }
      },
      { allowSignalWrites: true },
    );
  }

  private lastBtnIsFinish(): void {
    this.prevButtonHasChevron.set(true);
    this.nextButtonHasChevron.set(false);
    this.prevButtonText.set(this.perviousBtnTranslation);
    this.nextButtonText.set(this.finishBtnTranslation);
  }

  private setPrevNextBtn(): void {
    this.prevButtonHasChevron.set(true);
    this.nextButtonHasChevron.set(true);
    this.prevButtonText.set(this.perviousBtnTranslation);
    this.nextButtonText.set(this.nextBtnTranslation);
  }

  protected onStepSelection(index: number) {
    this.stepIndex.set(index + 1);
  }

  private setActive(): void {
    this.items.update(() =>
      this.items().map((item, itemIndex) => ({
        ...item,
        isActive: itemIndex + 1 === this.stepIndex(),
      })),
    );
  }

  protected onPreviousButtonClick(): void {
    if (this.stepIndex() === 1) {
      return;
    }
    this.stepIndex.set(this.stepIndex() - 1);
    this.setActive();
  }

  private handleNextOnFirstStep(): void {
    if (!this.legalPersonForm.disabled && !isFormValid(this.legalPersonForm)) {
      this.items.update(() =>
        this.items().map((item, itemIndex) => ({
          ...item,
          stepType: itemIndex === 0 ? StepType.hasWarrning : item.stepType,
        })),
      );
      return;
    }

    const formValues = this.legalPersonForm.getRawValue();

    this.legalPersonService
      .createLegalEntity({
        name: formValues.name,
        taxNumber: formValues.taxNumber,
        email: formValues.email,
        country: formValues.country,
        bankAccountNumber: formValues.bankAccountNumber,
        address: {
          zipCode: formValues.address.zipCode,
          city: formValues.address.city,
          publicSpace: formValues.address.publicSpace,
          publicSpaceType: formValues.address.publicSpaceType,
          houseNumber: formValues.address.houseNumber,
          doorOrFloor: formValues.address.doorOrFloor,
          countryCode: formValues.country,
        },
      })
      .subscribe((response) => {
        this.legalEntityId.set(response.id);
      });
    this.items.update(() =>
      this.items().map((item, itemIndex) => ({
        ...item,
        stepType:
          itemIndex === 1
            ? StepType.available
            : itemIndex === 0
              ? StepType.done
              : item.stepType,
      })),
    );
    this.stepIndex.set(2);
    this.setActive();
  }

  private professionalClientSelected(): void {
    this.items.set([
      ...this.defaultWizardSteps,
      {
        icon: '3',
        title: 'registrationWizard.proofOfProfessionalStatusForm.proofOfStatus',
        isActive: false,
        stepType: StepType.disabled,
        sectionType: RegistrationStepType.proofOfProfessionalStatus,
      },
    ]);
  }

  private handleNextOnSecondStep(): void {
    if (!this.documentUpload.disabled && !isFormValid(this.documentUpload)) {
      this.items.update(() =>
        this.items().map((item, itemIndex) => ({
          ...item,
          stepType: itemIndex === 1 ? StepType.hasWarrning : item.stepType,
        })),
      );
      return;
    }
    if (this.items().length > 2) {
      this.items.update(() =>
        this.items().map((item, itemIndex) => ({
          ...item,
          stepType:
            itemIndex === 2
              ? StepType.available
              : itemIndex === 1
                ? StepType.done
                : item.stepType,
        })),
      );
      this.stepIndex.set(3);
      this.setActive();
      return;
    }
    const documentUploadRaw = this.documentUpload.getRawValue();
    const files = [
      {
        uploadedFile: documentUploadRaw.authPartner.file,
        uploadedFileDescription: documentUploadRaw.authPartner.description,
        uploadedFileIsPublic: false,
        uploadedFileType: FileType.userLegalEntityRightDocuPoAAuthorizedPartner,
      } as FileData,
      {
        uploadedFile: documentUploadRaw.authRepresentative.file,
        uploadedFileDescription:
          documentUploadRaw.authRepresentative.description,
        uploadedFileIsPublic: false,
        uploadedFileType:
          FileType.userLegalEntityRightDocuPoAAuthorizedRepresentative,
      } as FileData,
      {
        uploadedFile: documentUploadRaw.signature.file,
        uploadedFileDescription: documentUploadRaw.signature.description,
        uploadedFileIsPublic: false,
        uploadedFileType:
          FileType.userLegalEntityRightDocuAuthorizedRepresentativeSignatureSpecimen,
      } as FileData,
    ];
    const legalEntityId = this.legalEntityId();
    const userId = this.authStore.user()?.id;

    if (legalEntityId && userId) {
      this.legalPersonService
        .postApprovalRequest(legalEntityId, {
          files: files,
          userId: userId,
          represents: ApprovalRepresentationTypes.LEGAL_PERSON,
        })
        .subscribe((response) => {
          this.router.navigate(['/profile']);
        });
    }
  }

  private handleNextOnThird(): void {
    if (!this.documentUpload.disabled && !isFormValid(this.documentUpload)) {
      this.items.update(() =>
        this.items().map((item, itemIndex) => ({
          ...item,
          stepType: itemIndex === 1 ? StepType.hasWarrning : item.stepType,
        })),
      );
      return;
    }
    const documentUploadRaw = this.documentUpload.getRawValue();

    const professionalRaw = this.proofOfProfessionalStatus.getRawValue();

    const files = [
      {
        uploadedFile: documentUploadRaw.signature.file,
        uploadedFileDescription: documentUploadRaw.signature.description,
        uploadedFileIsPublic: false,
        uploadedFileType:
          FileType.userLegalEntityRightDocuAuthorizedRepresentativeSignatureSpecimen,
      } as FileData,
      {
        uploadedFile: documentUploadRaw.authPartner.file,
        uploadedFileDescription: documentUploadRaw.authPartner.description,
        uploadedFileIsPublic: false,
        uploadedFileType: FileType.userLegalEntityRightDocuPoAAuthorizedPartner,
      } as FileData,
      {
        uploadedFile: documentUploadRaw.authRepresentative.file,
        uploadedFileDescription:
          documentUploadRaw.authRepresentative.description,
        uploadedFileIsPublic: false,
        uploadedFileType:
          FileType.userLegalEntityRightDocuPoAAuthorizedRepresentative,
      } as FileData,
      {
        uploadedFile: professionalRaw.file,
        uploadedFileDescription: professionalRaw.description,
        uploadedFileIsPublic: false,
        uploadedFileType: FileType.userLegalEntityRightDocuProfessional,
      } as FileData,
    ];
    const legalEntityId = this.legalEntityId();
    const userId = this.authStore.user()?.id;

    if (legalEntityId && userId) {
      this.legalPersonService
        .postApprovalRequest(legalEntityId, {
          files: files,
          userId: userId,
          represents: ApprovalRepresentationTypes.PROFESSIONAL_CLIENT,
        })
        .subscribe((response) => {
          this.router.navigate(['/profile']);
        });
    }
  }

  protected onNextButtonClick() {
    switch (this.stepIndex()) {
      case 1:
        this.handleNextOnFirstStep();
        break;
      case 2:
        this.handleNextOnSecondStep();
        break;
      case 3:
        this.handleNextOnThird();
        break;
    }
  }
}
